import React from "react";
import { find } from "lodash";
import { useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";

import { NavigationConfigId } from "../../../../commons/config/customer-groups";
import { getSpecConfig } from "../../../../commons/specs";
import {
  CategoryGroupDefinition,
  CategoryGroupKeyMap,
  CategoryMap,
  ProductSpecKey,
  ProductType
} from "../../../../commons/specs/product";
import { GlobalLocationState } from "../../../../commons/types/location";
import landscapeIllustration from "../../../../resources/images/product/illustration-landscape.png";
import CategoryListItem from "../../components/CategoryListItem/CategoryListItem";
import CenteredContent from "../../components/CenteredContent/CenteredContent";
import DistributedContent from "../../components/DistributedContent/DistributedContent";
import Headline from "../../components/Headline/Headline";
import ItemList from "../../components/ItemList/ItemList";
import LoadingIndicator from "../../components/LoadingIndicator/LoadingIndicator";
import MultiColumnItemList from "../../components/MultiColumnItemList/MultiColumnItemList";
import { addActiveFilterValue, hasActiveFilters, isActiveFilter, removeActiveFilterValue } from "../../libs/filters";
import { useAppHistory, useAppLocation } from "../../libs/hooks/use-app-routing";
import useEnv from "../../libs/queries/use-env";
import useProductFilter from "../../libs/queries/use-product-filter";
import useProducts from "../../libs/queries/use-products";
import { ROUTES } from "../../routes";

import FooterContentPartial from "./FooterContentPartial";

interface Props extends RouteComponentProps<EmptyObject, EmptyObject, GlobalLocationState> {
  productFilter: ReturnType<typeof useProductFilter>;
}

const backgroundImageMap: Record<NavigationConfigId, string | undefined> = {
  [NavigationConfigId.Bicycle]: landscapeIllustration,
  [NavigationConfigId.Motorcycle]: undefined
};

const CategoriesContent = ({ productFilter }: Props) => {
  const location = useAppLocation();
  const history = useAppHistory();
  const { t } = useTranslation(["commons"]);

  const env = useEnv();

  const products = useProducts({
    productType: env.productType,
    activeFilters: productFilter.activeFilters,
    limit: 0
  });

  const specConfigs = env.customerGroupConfig.productTypes.map(getSpecConfig);

  const allCategoryGroups = specConfigs.reduce((acc, specConfig) => {
    return [...acc, ...specConfig.categoryGroups];
  }, [] as CategoryGroupDefinition[]);

  const renderGroups = allCategoryGroups
    .map(group => {
      const categories = group.categories.filter(category => {
        const categoryFilterValues = productFilter.query.data?.[ProductSpecKey.CategoryKey];
        return find(categoryFilterValues, filterValue => filterValue.value === category);
      });

      if (categories.length === 0) {
        return null;
      }

      return (
        <React.Fragment key={group.key}>
          <Headline>
            {t(
              `commons:specs.${group.productType}.categoryGroup.${group.key}` as keyof {
                [T in ProductType as `commons:specs.${T}.categoryGroup.${CategoryGroupKeyMap[T]}`]: null;
              }
            )}
          </Headline>
          <ItemList>
            {categories.map(category => {
              const active = isActiveFilter(productFilter.activeFilters, ProductSpecKey.CategoryKey, category);

              return (
                <CategoryListItem
                  key={category}
                  label={t(
                    `commons:specs.${group.productType}.categories.${category}.default` as keyof {
                      [T in ProductType as `commons:specs.${T}.categories.${CategoryMap[T]}.default`]: null;
                    }
                  )}
                  active={active}
                  onClick={() =>
                    productFilter.setActiveFilters(
                      active
                        ? removeActiveFilterValue(productFilter.activeFilters, ProductSpecKey.CategoryKey, category)
                        : addActiveFilterValue(productFilter.activeFilters, ProductSpecKey.CategoryKey, category)
                    )
                  }
                />
              );
            })}
          </ItemList>
        </React.Fragment>
      );
    })
    .filter(group => !!group);

  return (
    <DistributedContent
      imageSrc={backgroundImageMap[env.customerGroupConfig.navigation]}
      footer={
        <FooterContentPartial
          isLoading={products.query.isFetching}
          activeFilters={productFilter.activeFilters}
          activeFiltersCount={productFilter.activeFiltersCount}
          showsOnlyEBikes={productFilter.showsOnlyEBikes}
          resultCount={products.query.data?.totalFiltered ?? 0}
          onFilterChange={productFilter.setActiveFilters}
          onFilterReset={productFilter.clearAllFilters}
          onSubmit={() => {
            history.push(ROUTES.PRODUCT_FINDER.INDEX, {
              ...(location.state ?? {}),
              // If `goBackToDetails` is set, we know the user visited details content page before
              // We reset that state to prevent the user from being redirected to details page after submitting the searchTerm
              goBackToDetails: undefined
            });
          }}
          isFilterResetButtonDisabled={!hasActiveFilters(productFilter.activeFilters)}
        />
      }
    >
      <CenteredContent
        childrenWidth={productFilter.query.isFetching ? "auto" : "full"}
        classNames={["u-padding-top-xxl", "u-space-xxl-media-s-to-l"]}
      >
        {productFilter.query.isFetching ? (
          <LoadingIndicator classNames={["u-padding-top-xxxxl"]} key="loading-indicator" />
        ) : (
          <MultiColumnItemList gap={renderGroups.length < 4 ? "xxl" : "base"}>{renderGroups}</MultiColumnItemList>
        )}
      </CenteredContent>
    </DistributedContent>
  );
};

export default CategoriesContent;
